import { useState } from "react";
import { api, CONTACT_INFO, formatApiError } from "../../lib/api";
import { MapPin, Phone, Mail, Send, CheckCircle2 } from "lucide-react";

export default function Contact() {
  const [form, setForm] = useState({ name: "", email: "", phone: "", subject: "", message: "" });
  const [sent, setSent] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const handle = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    try {
      await api.post("/contact", form);
      setSent(true);
      setForm({ name: "", email: "", phone: "", subject: "", message: "" });
      setTimeout(() => setSent(false), 5000);
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || "Failed to send message");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="contact" className="py-24 bg-[#F2EFEB]" data-testid="contact-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="max-w-2xl mb-14">
          <div className="overline text-[#E07A5F]">Get in Touch</div>
          <h2 className="font-serif-display text-4xl sm:text-5xl text-[#1B4332] mt-3 leading-tight">
            Contact Us
          </h2>
          <p className="mt-4 text-[#5C6661] leading-relaxed">
            Have questions about our courses, certificates, or programs? We'd love to hear from you.
          </p>
        </div>

        <div className="grid lg:grid-cols-12 gap-6">
          <div className="lg:col-span-5 space-y-4">
            <InfoCard icon={MapPin} label="Visit Us" value={CONTACT_INFO.address} testid="contact-address" />
            <InfoCard icon={Phone} label="Call Us" value={CONTACT_INFO.phone} href={`tel:${CONTACT_INFO.phone}`} testid="contact-phone" />
            <InfoCard icon={Mail} label="Email Us" value={CONTACT_INFO.email} href={`mailto:${CONTACT_INFO.email}`} testid="contact-email" />

            <div className="bg-[#1B4332] rounded-2xl p-7 text-white">
              <h3 className="font-serif-display text-2xl">Office Hours</h3>
              <div className="mt-4 space-y-2 text-sm text-stone-200">
                <div className="flex justify-between"><span>Monday – Saturday</span><span className="font-medium text-white">9:00 AM – 6:00 PM</span></div>
                <div className="flex justify-between"><span>Sunday</span><span className="font-medium text-white">Closed</span></div>
              </div>
            </div>
          </div>

          <form
            onSubmit={submit}
            className="lg:col-span-7 bg-white rounded-2xl p-7 lg:p-9 border border-border"
            data-testid="contact-form"
          >
            <div className="grid sm:grid-cols-2 gap-4">
              <Field label="Your Name" required value={form.name} onChange={handle("name")} testid="contact-name-input" />
              <Field label="Email Address" required type="email" value={form.email} onChange={handle("email")} testid="contact-email-input" />
              <Field label="Phone (optional)" value={form.phone} onChange={handle("phone")} testid="contact-phone-input" />
              <Field label="Subject" value={form.subject} onChange={handle("subject")} testid="contact-subject-input" />
            </div>
            <div className="mt-4">
              <label className="text-xs uppercase tracking-widest text-[#5C6661] font-semibold">Message *</label>
              <textarea
                required
                value={form.message}
                onChange={handle("message")}
                rows={5}
                className="mt-1.5 w-full px-4 py-3 border border-border rounded-xl text-sm focus:outline-none focus:border-[#1B4332] focus:ring-1 focus:ring-[#1B4332] transition-colors resize-none"
                placeholder="Tell us how we can help..."
                data-testid="contact-message-input"
              />
            </div>

            {sent && (
              <div className="mt-4 bg-green-50 border border-green-200 text-green-800 rounded-xl p-3 flex items-center gap-2 text-sm" data-testid="contact-success">
                <CheckCircle2 className="w-4 h-4" /> Message sent successfully! We'll get back to you soon.
              </div>
            )}
            {error && (
              <div className="mt-4 bg-red-50 border border-red-200 text-red-700 rounded-xl p-3 text-sm" data-testid="contact-error">{error}</div>
            )}

            <button
              type="submit"
              disabled={loading}
              className="mt-6 inline-flex items-center gap-2 bg-[#1B4332] text-white px-7 py-3 rounded-full text-sm font-semibold hover:bg-[#2D6A4F] transition-colors disabled:opacity-50"
              data-testid="contact-submit-btn"
            >
              {loading ? "Sending..." : "Send Message"} <Send className="w-4 h-4" />
            </button>
          </form>
        </div>
      </div>
    </section>
  );
}

function InfoCard({ icon: Icon, label, value, href, testid }) {
  const inner = (
    <>
      <div className="w-10 h-10 rounded-xl bg-[#E07A5F]/10 text-[#E07A5F] flex items-center justify-center shrink-0">
        <Icon className="w-5 h-5" />
      </div>
      <div>
        <div className="overline text-[#5C6661]">{label}</div>
        <div className="mt-1 text-[#222B26] font-medium">{value}</div>
      </div>
    </>
  );
  return href ? (
    <a href={href} className="bg-white rounded-2xl p-5 border border-border hover:border-[#E07A5F]/40 transition-colors flex items-start gap-4" data-testid={testid}>
      {inner}
    </a>
  ) : (
    <div className="bg-white rounded-2xl p-5 border border-border flex items-start gap-4" data-testid={testid}>
      {inner}
    </div>
  );
}

function Field({ label, required, type = "text", value, onChange, testid }) {
  return (
    <div>
      <label className="text-xs uppercase tracking-widest text-[#5C6661] font-semibold">
        {label}{required && " *"}
      </label>
      <input
        required={required}
        type={type}
        value={value}
        onChange={onChange}
        className="mt-1.5 w-full px-4 py-3 border border-border rounded-xl text-sm focus:outline-none focus:border-[#1B4332] focus:ring-1 focus:ring-[#1B4332] transition-colors"
        data-testid={testid}
      />
    </div>
  );
}
